/**
 * ErrorDisplay Component
 * Full-screen error display for critical failures
 *
 * Extracted from App.tsx for better organization and testability
 */

import React from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

export interface AppError {
  /** Error message to display */
  message: string;
  /** Optional error code for debugging */
  code?: string;
  /** Timestamp when error occurred */
  timestamp: string;
}

// ============================================================================
// Props Interface
// ============================================================================

export interface ErrorDisplayProps {
  /** The error to display */
  error: AppError;
  /** Optional callback for retry action */
  onRetry?: () => void;
}

// ============================================================================
// Component
// ============================================================================

/**
 * ErrorDisplay - Full-screen centered error display
 *
 * Features:
 * - Clear error messaging
 * - Optional error code display
 * - Optional retry button
 * - Dark theme consistent with app design
 * - Glass card styling
 */
export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ error, onRetry }) => (
  <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
    <div className="glass-card rounded-xl p-6 sm:p-8 max-w-md w-full text-center">
      <AlertCircle size={48} className="mx-auto mb-4 text-red-400" />
      <h2 className="text-xl font-semibold mb-2 text-red-300">Something went wrong</h2>
      <p className="text-gray-400 mb-4">{error.message}</p>
      {error.code && (
        <p className="text-xs text-gray-600 mb-4">Error code: {error.code}</p>
      )}
      {onRetry && (
        <button
          onClick={onRetry}
          className="btn-primary flex items-center justify-center gap-2 mx-auto"
        >
          <RefreshCw size={18} />
          <span>Try Again</span>
        </button>
      )}
    </div>
  </div>
);

ErrorDisplay.displayName = 'ErrorDisplay';
